import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

const DetailRow = ({
  label,
  value,
}) => {
  return (
    <Box
      display="grid"
      gridTemplateColumns="140px minmax(0, 1fr)"
      gap={2}
      alignItems="start"
    >
      <Typography
        variant="body2"
        color="text.secondary"
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          overflowWrap: "anywhere",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
};

const formatHourlyRate = (
  hourlyRate
) => {
  if (
    hourlyRate === null ||
    hourlyRate === undefined ||
    hourlyRate === ""
  ) {
    return "Not set";
  }

  const numericRate = Number(
    hourlyRate
  );

  if (!Number.isFinite(numericRate)) {
    return "Not set";
  }

  return `£${numericRate.toFixed(
    2
  )} per hour`;
};

const RoleAssignmentDetailsModal = ({
  open,
  onClose,
  card,
  role,
  onRemove,
  onSetPrimary,
  removing = false,
  settingPrimary = false,
}) => {
  if (!card) {
    return null;
  }

  const tenantUser =
    card.tenantUser || null;

  const user =
    tenantUser?.user || null;

  const displayName =
    card.displayName ||
    tenantUser?.displayName ||
    `${user?.firstName || ""} ${
      user?.lastName || ""
    }`.trim() ||
    "Unknown user";

  const email =
    card.email ||
    tenantUser?.email ||
    user?.email ||
    "—";

  const hourlyRate =
    card.hourlyRate ??
    tenantUser?.hourlyRate ??
    user?.hourlyRate ??
    null;

  const roleName =
    role?.name ||
    card.tenantRole?.name ||
    "Unknown role";

  const isPrimary =
    card.isPrimary ??
    card.assignment?.isPrimary ??
    false;

  const assignmentId =
    card.assignment?.id || null;

  const tenantUserStatus =
    tenantUser?.status ||
    (tenantUser?.isActive === false
      ? "Inactive"
      : "Active");

  const handleRemove = () => {
    if (
      !assignmentId ||
      removing ||
      settingPrimary
    ) {
      return;
    }

    onRemove?.({
      assignmentId,
      card,
      role,
    });
  };

  const handleSetPrimary = () => {
    if (
      !assignmentId ||
      isPrimary ||
      removing ||
      settingPrimary
    ) {
      return;
    }

    onSetPrimary?.({
      assignmentId,
      card,
      role,
    });
  };

  const handleClose = () => {
    if (
      removing ||
      settingPrimary
    ) {
      return;
    }

    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Role Assignment Details
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
            >
              {displayName}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              {roleName}
            </Typography>
          </Box>

          <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
          >
            <Chip
              label={
                isPrimary
                  ? "Primary role"
                  : "Secondary role"
              }
              color={
                isPrimary
                  ? "primary"
                  : "default"
              }
              size="small"
            />

            <Chip
              label={tenantUserStatus}
              variant="outlined"
              size="small"
            />
          </Box>

          <Divider />

          <Stack spacing={1.5}>
            <DetailRow
              label="Person"
              value={displayName}
            />

            <DetailRow
              label="Email"
              value={email}
            />

            <DetailRow
              label="Role"
              value={roleName}
            />

            <DetailRow
              label="Assignment"
              value={
                isPrimary
                  ? "Primary role"
                  : "Secondary role"
              }
            />

            <DetailRow
              label="Hourly rate"
              value={formatHourlyRate(
                hourlyRate
              )}
            />
          </Stack>

          <Alert severity="warning">
            Removing this assignment may
            reduce role coverage and leave
            SOP responsibilities without an
            assigned person.
          </Alert>

          {isPrimary && (
            <Alert severity="error">
              This is the person&apos;s
              primary role. Removing it may
              leave them without a primary
              role.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={
            removing ||
            settingPrimary
          }
          variant="outlined"
        >
          Close
        </Button>

        {!isPrimary &&
          onSetPrimary && (
            <Button
              onClick={
                handleSetPrimary
              }
              disabled={
                removing ||
                settingPrimary ||
                !assignmentId
              }
              variant="outlined"
              color="primary"
              startIcon={
                settingPrimary ? (
                  <CircularProgress
                    size={16}
                    color="inherit"
                  />
                ) : null
              }
            >
              {settingPrimary
                ? "Updating..."
                : "Make primary"}
            </Button>
          )}

        <Button
          onClick={handleRemove}
          disabled={
            removing ||
            settingPrimary ||
            !assignmentId
          }
          variant="contained"
          color="error"
          startIcon={
            removing ? (
              <CircularProgress
                size={16}
                color="inherit"
              />
            ) : null
          }
        >
          {removing
            ? "Removing..."
            : "Remove from role"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleAssignmentDetailsModal;